import { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { ApplySettings } from "../controls/ApplySettingsRadioGroup";
import { useApplySettings } from "../../hooks/useApplySettings";
import { useTemplatesStatusStore } from "../../store/useTemplatesStatusStore";
import { ItemType, TemplateState } from "../../types";
import { ApplySettingsDrawer } from "../StoryboardMenuTabs/ApplySettingsDrawer";
import { ApplySettingsOptions } from "../controls/ApplySettingsOptions";

export const ApplySettingsFeature = ({
  partialTemplateState,
  itemType,
  applyFn,
  allSceneTypes = false,
}: {
  partialTemplateState: Partial<TemplateState>;
  itemType: ItemType["type"];
  applyFn?: (
    templateIds: string[],
    partialTemplateState: Partial<TemplateState>
  ) => void;
  // Apply to scenes of any type instead of only the current scene's type.
  allSceneTypes?: boolean;
}) => {
  const t = useTranslate();

  const [openSettingsDrawer, setOpenSettingsDrawer] = useState(false);

  const { handleApplyToAll, handleApplyToSome } = useApplySettings();

  const [checkedChapters, setCheckedChapters] = useState<string[]>([]);

  const { addBulkTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addBulkTemplateUpdate: state.addBulkTemplateUpdate,
  }));

  const handleApplySettingsSubmit = (templateIds: string[]) => {
    handleApplyToSome(templateIds, partialTemplateState, applyFn);
    addBulkTemplateUpdate();
    setCheckedChapters([]);
    setOpenSettingsDrawer(false);
  };

  const handleApplySettingsChange = (value: ApplySettings) => {
    if (value === ApplySettings.SPECIFIC) {
      setOpenSettingsDrawer(true);
    } else {
      if (openSettingsDrawer) {
        setOpenSettingsDrawer(false);
      }
      handleApplyToAll(partialTemplateState, applyFn, allSceneTypes);
      addBulkTemplateUpdate();
    }
  };

  return (
    <>
      <ApplySettingsDrawer
        open={openSettingsDrawer}
        title={
          allSceneTypes
            ? t("components.templates.settings.applySettings.allScenesTitle", {
                item: t(
                  `components.templates.settings.applySettings.items.${itemType}`
                ),
                type: t(
                  "components.templates.settings.applySettings.types.specific"
                ),
              })
            : t("components.templates.settings.applySettings.title", {
                type: t(
                  "components.templates.settings.applySettings.types.specific"
                ),
              })
        }
        onClose={() => setOpenSettingsDrawer(false)}
        getContainer={() => {
          const scene =
            document.getElementsByClassName("video-editor-scene")[0];
          if (scene) {
            return scene;
          }
          return document.body;
        }}
        rootStyle={{
          position: "fixed",
        }}
        onApplySettings={handleApplySettingsSubmit}
        checkedChapters={checkedChapters}
        setCheckedChapters={setCheckedChapters}
        allSceneTypes={allSceneTypes}
        maskClosable={true}
        mask={true}
      />
      <ApplySettingsOptions
        onChange={handleApplySettingsChange}
        itemType={itemType}
        allSceneTypes={allSceneTypes}
      />
    </>
  );
};
